import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import {
  People,
  Storefront,
  Agriculture,
  MonetizationOn,
  ShoppingBasket,
  TrendingUp,
  Map,
  Grain,
  AssignmentTurnedIn,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import Chart from "react-apexcharts";
import { Spin } from "antd";

import { selectCurrentUser } from "features/auth/authSlice";
import { useGetDashboardProfilebyIdQuery } from "features/onboarding/organizations/FarmersGroup/FPO/FPOApiSlice";
import FarmerList from "features/onboarding/users/farmer/FarmerList";
import EmandiReports from "features/E-Mandi/EmandiReport/emandiReport";
import { Mandi } from "features/E-Mandi/EmandiReport/Mandi";
import EmployeesList from "features/userManagement/EmployeesList";
import { AggregationParent } from "components/Aggregation/AggregationParent";
import ClusterMap from "../OrganizationDashboard/ClusterMap";
import { FieldActivities } from "./FieldActivities";
import { store } from "app/store";
import { API_URL } from "app/config";
import { ProjectCommodities } from "./ProjectCommodities";
import HclProjectFarmerNewComponent from "./HclProjectFarmerNewComponent";
import { FieldActivitiesNewUi } from "./FieldActivitiesNewUi";
import { useTranslation } from "react-i18next";
import { CMS_maati } from "urlconfig";

const ProjectNewCustomUiDesign = ({
  activeCards,
  activeProjects,
  orgName,
  orgAdmin,
}) => {
  const { id } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const isCBBO = location.pathname.includes("CBBO");
  const currentUser = useSelector(selectCurrentUser);
  const { data: userProfile, isLoading } = useGetDashboardProfilebyIdQuery(id);
  const [apiData, setApiData] = useState({});
  const [activeMenu, setActiveMenu] = useState(null);
  const [filteredTabs, setFilteredTabs] = useState([]);
  const [mandiCard, setMandiCard] = useState({
    total: "",
    sum: "",
    totalQuantity: 0,
  });
  const [totalFarmers, setTotalFarmers] = useState("");
  useEffect(() => {
    if (userProfile?.data) setApiData(userProfile.data);
  }, [userProfile]);
  // const formatProcurement = () => {
  //   const quantity = mandiCard?.totalQuantity || 0;
  //   if (quantity < 1000) return `${Math.round(quantity).toLocaleString()} Kg`;
  //   if (quantity < 10000)
  //     return `${Math.round(quantity / 100).toLocaleString()} Quintal`;
  //   return `${Math.round(quantity / 1000).toLocaleString()} MT`;
  // };
  const fetchMandiData = async () => {
    try {
      const state = store.getState();
      const response = await fetch(
        `${API_URL}/api/v1/onboarding/farmer-aggrigation?organization_id=${id}`,
        {
          headers: { authorization: `Bearer ${state.auth.accessToken}` },
        },
      );
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      const filteredData = data?.data?.filter(
        (item) => item.current_status === "Procurement Done",
      );
      const totalQuantity = filteredData?.reduce((sum, item) => {
        let quantity = Number(item.quantity || 0);
        if (item.unit_of_quantity === "Quintals") quantity *= 100;
        return sum + quantity;
      }, 0);
      setMandiCard({ total: data?.data || [], totalQuantity });
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  useEffect(() => {
    fetchMandiData();
  }, []);
  const fetchFarmers = async (url) => {
    try {
      const response = await fetch(url);
      const data = await response.json();
      setTotalFarmers(data?.meta?.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to fetch farmers", err);
    }
  };

  const project = apiData?.organization_details?.project || null;
  const modules = project?.modules || {};
  const firstState = project?.states?.[0]?.label || "";
  // const firstDistrict = project?.districts?.[0]?.label || "";
  const rawDistrict = project?.districts?.[0]?.label || "";

  const firstDistrict =
    rawDistrict.toLowerCase() === "bara banki" ? "Barabanki" : rawDistrict;
  const projectName = project?.project_name || "";
  const firstStateCode = project?.states?.[0]?.value || "";
  const firstDistrictCode = project?.districts?.[0]?.value || "";
  useEffect(() => {
    fetchFarmers(
      `${CMS_maati}/api/farmer-basic-details?pagination[pageSize]=25&filters[district]=${firstDistrict}`,
    );
  }, [firstDistrict]);

  const menuItems = useMemo(() => {
    if (!project && !isCBBO) return [];

    const items = [
      {
        key: "Cluster Map",
        label: "Cluster Map",
        icon: <Map />,
        component: (
          <HclProjectFarmerNewComponent
            showMap={true}
            projectExist={project || {}}
            firstState={project?.states?.[0]?.label || ""}
            firstDistrict={project?.districts?.[0]?.label || ""}
            firstStateCode={project?.states?.[0]?.value || ""}
            firstDistrictCode={project?.districts?.[0]?.value || ""}
          />
        ),
        color: "#FF8E53",
      },
      {
        key: "Commodities",
        label: "Commodities",
        icon: <Grain />,
        component: (
          <ProjectCommodities
            selectedCommodities={
              apiData?.organization_details?.project?.selectedCommodities
            }
          />
        ),
        color: "#FF8E53",
      },

      {
        key: "Live Mandi Prices",
        label: "Live Mandi Prices",
        icon: <TrendingUp />,
        component: (
          <EmandiReports
            isHCL={true}
            crop=""
            firstState={firstState}
            firstDistrict={firstDistrict}
          />
        ),
        color: "#FF8E53",
      },
      {
        key: "E-Mandi",
        label: "E-Mandi",
        icon: <TrendingUp />,
        component: <Mandi isHCL />,
        color: "#FF8E53",
      },
      {
        key: "Farmers",
        label: "Farmers",
        icon: <People />,
        component: (
          <HclProjectFarmerNewComponent
            showMap={false}
            projectExist={project || {}}
            firstDistrict={project?.districts?.[0]?.label}
          />
        ),
        color: "#FF8E53",
      },
      {
        key: "Aggregation",
        label: "Aggregation",
        icon: <Agriculture />,
        component: <AggregationParent crops={apiData?.crops_dealing} />,
        color: "#2EC4B6",
      },
      {
        key: "Employees",
        label: "Employees",
        icon: <People />,
        component: <EmployeesList />,
        color: "#FFBF69",
      },
    ];
    if (
      (projectName &&
        projectName.toLowerCase() ===
          "project maati - baseline assessment of value chain" &&
        orgAdmin) ||
      (projectName &&
        projectName.toLowerCase() !==
          "project maati - baseline assessment of value chain")
    ) {
      items.push({
        key: "Field Activities",
        label: "Field Activities",
        icon: <Agriculture />,
        component: (
          <FieldActivities
            subProjectData={
              apiData?.organization_details?.project?.uploadedFiles
            }
          />
        ),
        color: "#FF8E53",
      });
    }
    if (
      projectName &&
      projectName.toLowerCase() ===
        "project maati - baseline assessment of value chain"
    ) {
      items.push({
        key: "Baseline Assessment",
        label: "Baseline Assessment",
        icon: <AssignmentTurnedIn />,
        component: (
          <FieldActivitiesNewUi
            subProjectData={
              apiData?.organization_details?.project?.uploadedFiles || []
            }
            orgName={orgName}
            firstState={firstState}
            firstDistrict={firstDistrict}
            firstStateCode={firstStateCode}
            firstDistrictCode={firstDistrictCode}
          />
        ),
        color: "#FF8E53",
      });
    }
    const baselineIndex = items.findIndex(
      (item) => item.key === "Baseline Assessment",
    );
    if (baselineIndex > -1) {
      const [baselineItem] = items.splice(baselineIndex, 1);
      items.unshift(baselineItem);
    }

    return items;
  }, [project, apiData]);

  useEffect(() => {
    let tabs = [];

    if (project && Object.keys(modules).length > 0) {
      tabs = menuItems
        .filter(
          (item) =>
            Object.keys(modules).includes(item.key) ||
            // item.key === "Live Mandi Prices"
            item.key === "Baseline Assessment",
        )
        .map((item) => ({
          ...item,
          label: modules[item.key] || item.label,
        }));
    } else if (isCBBO) {
      tabs = menuItems.filter((item) =>
        ["Farmers", "E-Mandi", "Live Mandi Prices"].includes(item.key),
      );
    } else {
      tabs = menuItems.filter((item) =>
        [
          "Farmers",
          "E-Mandi",
          "Live Mandi Prices",
          "Employees",
          "Aggregation",
        ].includes(item.key),
      );
    }

    setFilteredTabs(tabs);
  }, [menuItems, project, modules]);
  useEffect(() => {
    if (filteredTabs.length > 0 && !activeMenu) setActiveMenu(filteredTabs[0]);
  }, [filteredTabs, activeMenu]);
  const isBaselineProject =
    projectName === "Project MAATI - Baseline Assessment of Value Chain";

  console.log(project?.sub_project?.project_name, "cecllll");
  const cards = [
    {
      title: "Commodities",
      icon: "https://onboarding.agrani.io/assets/wheat-Cd8AZQ9X.png",
      total:
        apiData?.organization_details?.project?.selectedCommodities?.length ??
        0,
    },

    {
      title: "Land Holding",
      icon: "https://onboarding.agrani.io/assets/acerage-CbrTaoRH.png",
      total:
        project?.sub_project?.project_name === "Fazenda Santa Fe Do Piaui"
          ? "5500 Hectares"
          : apiData?.land_holding ?? 0,
    },

    ...(isBaselineProject
      ? [
          {
            title: "Surveys",
            icon: "https://onboarding.agrani.io/assets/farmers-CTZXnfqs.png",
            total: totalFarmers || 0,
          },
        ]
      : []),
  ];

  const chartConfigMap = {
    Farmers: {
      color: "#FF8E53",
      type: "area",
      data: [10, 15, 12, 18, 22, 25, 28],
    },
    "Land Holding": {
      color: "#6C63FF",
      type: "bar",
      data: [9, 10, 10, 10, 10, 10, 12],
    },
    Commodities: {
      color: "#2EC4B6",
      type: "area",
      data: [8, 12, 15, 20, 18, 22, 25],
    },
    Employees: { color: "#FFBF69", type: "line", data: [1, 3, 5, 7, 8, 10] },
    "E-Mandi": { color: "#FF6F61", type: "area", data: [2, 4, 6, 8, 10, 12] },
  };

  const getChartOptions = (color) => ({
    chart: { sparkline: { enabled: true } },
    stroke: { curve: "smooth", width: 2 },
    fill: { opacity: 0.5 },
    colors: [color],
    tooltip: { enabled: false },
  });

  if (isLoading)
    return (
      <Spin
        size="large"
        style={{ marginTop: 50, display: "block", textAlign: "center" }}
      />
    );

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 1, lg: 3 } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: 3,
          mb: 4,
        }}
      >
        {cards.map((stat, i) => {
          const config =
            chartConfigMap[stat.title] || chartConfigMap["Farmers"];
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.2 }}
            >
              <Box
                sx={{
                  background: `linear-gradient(145deg, #fff, ${config.color}15)`,
                  borderRadius: 4,
                  p: 3,
                  height: 140,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow: `0 2px 2px ${config.color}25`,
                  "&:hover": {
                    transform: "translateY(-6px) scale(1.03)",
                    boxShadow: `0 1px 2px ${config.color}40`,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography fontWeight={600}>{t(stat.title)}</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {stat.total}
                  </Typography>
                </Box>
                <Chart
                  options={getChartOptions(config.color)}
                  series={[{ data: config.data }]}
                  type={config.type}
                  height={50}
                />
              </Box>
            </motion.div>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 3,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: 280 },
            backdropFilter: "blur(16px)",
            background: "rgba(255,255,255,0.75)",
            borderRadius: 3,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            position: "sticky",
            top: 20,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: "#333",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {t("Quick Access")}
          </Typography>
          {filteredTabs.map((item, i) => {
            const isActive = activeMenu?.label === item.label;
            return (
              <motion.div
                key={i}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                onClick={() => setActiveMenu(item)}
                style={{
                  padding: "14px 16px",
                  borderRadius: "14px",
                  background: isActive
                    ? `linear-gradient(135deg, ${item.color} 0%, ${item.color}99 100%)`
                    : "#fff",
                  color: isActive ? "#fff" : "#333",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  fontWeight: 600,
                  fontSize: "15px",
                  boxShadow: isActive
                    ? `0 6px 18px ${item.color}50`
                    : "0 2px 8px rgba(0,0,0,0.05)",
                  cursor: "pointer",
                  transition: "all 0.25s ease",
                }}
              >
                <Box sx={{ fontSize: "20px" }}>{item.icon}</Box>
                <span>{t(item.label)}</span>
              </motion.div>
            );
          })}
        </Box>

        <Box
          sx={{
            flex: 1,
            background: "rgba(255,255,255,0.9)",
            borderRadius: 3,
            p: { xs: 1, lg: 3 },
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            animation: "fadeIn 0.4s ease",
            overflow: "hidden",
            width: "100%",
          }}
        >
          {activeMenu?.component || (
            <Typography>Select a section from the sidebar.</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectNewCustomUiDesign;
